import { normalizeEmailLinkUrl } from "./emailSafeLink";
import { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CmpNewsletterFieldsFragment } from "../../../graphql/types";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import { DataTable } from "../../common/dashboard/DataTable";
import {
  buildEmailOpenListCsv,
  nextEmailManageSort,
  EmailManageSort,
} from "./emailManageActions";
import {
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from "./emailWizardShared";

type Analytics = NonNullable<CmpNewsletterFieldsFragment["analytics"]>;
// Design header separator: " &nbsp;•&nbsp; ".
const designSeparator = " \u00A0\u2022\u00A0 ";
const percentage = (count: number, total: number) =>
  total ? `${Math.round((count / total) * 100)}%` : "0%";
const date = (value?: number | null, timeZone?: string | null) =>
  value
    ? new Date(value).toLocaleString("en-CA", {
        timeZone: timeZone || "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "Unavailable";

export const EmailEngagementTables = ({
  analytics,
}: {
  analytics: Analytics;
}) => (
  <div className="mt-6 grid gap-6 lg:grid-cols-2">
    <section className="min-w-0 rounded-xl border border-[#ECECEC] px-5 py-[26px] sm:px-7">
      <h2 className="mb-4 text-[18px] font-semibold">Top Links Clicked</h2>
      <DataTable
        ariaLabel="Top links clicked"
        rows={analytics.links}
        getRowKey={(row) => row.url}
        columns={[
          {
            key: "url",
            header: "Link",
            render: (row) => (
              <span className="flex flex-col gap-1">
                <span className="font-semibold">{row.label}</span>
                <a
                  className="break-all text-[12.5px] text-blue-600"
                  href={normalizeEmailLinkUrl(row.url) || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {row.url}
                </a>
              </span>
            ),
          },
          {
            key: "clicks",
            header: "Clicks",
            render: (row) => row.clickCount ?? "Unavailable",
          },
          {
            key: "unique",
            header: "Unique Clicks",
            render: (row) => row.uniqueClickCount ?? "Unavailable",
          },
        ]}
        showEmptyRow={!analytics.links.length}
        emptyRowContent="No one has clicked a link yet."
        containerClassName="overflow-x-auto"
      />
    </section>
    <section className="min-w-0 rounded-xl border border-[#ECECEC] px-5 py-[26px] sm:px-7">
      <h2 className="mb-4 text-[18px] font-semibold">Audience Breakdown</h2>
      <DataTable
        ariaLabel="Audience breakdown"
        rows={analytics.audiences}
        getRowKey={(row) => row.label}
        columns={[
          { key: "label", header: "Audience", render: (row) => row.label },
          {
            key: "recipients",
            header: "Recipients",
            render: (row) => row.recipientCount,
          },
          {
            key: "opens",
            header: "Open Rate",
            render: (row) => percentage(row.openCount, row.recipientCount),
          },
          {
            key: "clicks",
            header: "Click Rate",
            render: (row) => percentage(row.clickCount, row.recipientCount),
          },
        ]}
        showEmptyRow={!analytics.audiences.length}
        emptyRowContent="No delivery details yet."
        containerClassName="overflow-x-auto"
      />
    </section>
  </div>
);

export const EmailOpenListPage = ({
  newsletter,
  campaignName,
  loading,
  error,
  timeZone,
  onBack,
  onRetry,
}: {
  newsletter: CmpNewsletterFieldsFragment | null;
  campaignName: string;
  loading: boolean;
  error: string;
  timeZone?: string | null;
  onBack: () => void;
  onRetry: () => void;
}) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Recipients");
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [sort, setSort] = useState<EmailManageSort>({
    column: null,
    direction: "asc",
  });
  const rows = useMemo(() => {
    const filtered = (newsletter?.analytics?.recipients || []).filter(
      (row) =>
        [row.name, row.email, row.companyName]
          .join(" ")
          .toLowerCase()
          .includes(search.trim().toLowerCase()) &&
        (status === "All Recipients" ||
          (status === "Opened"
            ? !!row.firstOpenedAtUtcMilli
            : row.status === status))
    );
    const value = (row: (typeof filtered)[number]): string | number =>
      sort.column === "name"
        ? row.name || ""
        : sort.column === "email"
          ? row.email
          : sort.column === "company"
            ? row.companyName || ""
            : sort.column === "status"
              ? row.status
              : sort.column === "opens"
                ? (row.openCount ?? -1)
                : (row.lastOpenedAtUtcMilli ?? -1);
    return sort.column
      ? [...filtered].sort((a, b) => {
          const left = value(a);
          const right = value(b);
          return (
            (typeof left === "number" && typeof right === "number"
              ? left - right
              : String(left).localeCompare(String(right))) *
            (sort.direction === "asc" ? 1 : -1)
          );
        })
      : filtered;
  }, [newsletter, search, status, sort]);
  const exportRows = () => {
    const url = URL.createObjectURL(
      new Blob([buildEmailOpenListCsv(rows, timeZone || "UTC")], {
        type: "text/csv;charset=utf-8",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "subscriber-email-open-list.csv";
    link.click();
    URL.revokeObjectURL(url);
    setExportSuccess(`Open list for ${campaignName} has been exported.`);
  };
  return (
    <>
      <div className="border-b border-[#ECECEC] pb-4">
        <button
          type="button"
          className={`${secondaryButtonClassName} mb-4`}
          onClick={onBack}
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          Back to Manage Emails
        </button>
        <h1 className="text-[28px] font-semibold">
          Subscriber Email Open List
        </h1>
        <p className="mt-2 text-[15px]">
          {campaignName}
          {newsletter?.sentAtUtcMilli
            ? `${designSeparator}Sent on ${date(newsletter.sentAtUtcMilli, timeZone)}`
            : ""}
          {newsletter?.analytics
            ? `${designSeparator}${newsletter.analytics.openCount} of ${newsletter.analytics.recipientCount} subscribers opened`
            : ""}
        </p>
      </div>
      {loading && (
        <p role="status" className="mt-6">
          Loading recipients...
        </p>
      )}
      {error && (
        <div role="alert" className="mt-6 text-red-700">
          {error}{" "}
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      )}
      {!loading && !error && !newsletter?.analytics && (
        <p role="status" className="mt-6">
          Analytics are not available for this email yet. Refresh the page later to check again.
        </p>
      )}
      {!loading && !error && newsletter?.analytics && (
        <div className="mt-6 rounded-xl border border-[#ECECEC] bg-white px-5 py-[26px] sm:px-7">
          <div className="mb-[18px] flex flex-wrap items-center justify-between gap-[14px]">
            <div className="relative w-full max-w-[420px] sm:flex-1">
              <SearchIcon
                className="absolute left-[14px] top-1/2 -translate-y-1/2 text-[#9E9E9E]"
                sx={{ fontSize: 16 }}
              />
              <input
                aria-label="Search subscribers"
                placeholder="Search by Subscriber, Email or Company"
                className={`${inputClassName} !pl-10`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto sm:flex-nowrap">
              <select
                aria-label="Recipient status"
                className={`${inputClassName} !w-auto max-w-[200px]`}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {["All Recipients", "Opened", "Clicked", "Not Opened"].map(
                  (value) => (
                    <option key={value}>{value}</option>
                  )
                )}
              </select>
              <button
                type="button"
                className={primaryButtonClassName}
                onClick={exportRows}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />
                Export Open List
              </button>
            </div>
          </div>
          <DataTable
            ariaLabel="Subscriber email opens"
            rows={rows}
            getRowKey={(row) => row.id}
            sort={{
              column: sort.column || "",
              direction: sort.direction,
              onRequestSort: (column) =>
                setSort((current) => nextEmailManageSort(current, column)),
            }}
            columns={[
              {
                key: "name",
                header: "Subscriber",
                sortable: true,
                render: (row) => (
                  <span className="font-semibold">
                    {row.name || "Not recorded"}
                  </span>
                ),
              },
              {
                key: "email",
                header: "Email Address",
                sortable: true,
                render: (row) => row.email,
              },
              {
                key: "company",
                header: "Company",
                sortable: true,
                render: (row) => row.companyName || "Not recorded",
              },
              {
                key: "status",
                header: "Status",
                sortable: true,
                render: (row) => (
                  <span
                    className="whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold text-white"
                    style={{
                      backgroundColor:
                        row.status === "Clicked"
                          ? "#E1A731"
                          : row.status === "Opened"
                            ? "#10B981"
                            : "#9E9E9E",
                    }}
                  >
                    {row.status}
                  </span>
                ),
              },
              {
                key: "opens",
                header: "Opens",
                sortable: true,
                render: (row) => row.openCount ?? "Unavailable",
              },
              {
                key: "last",
                header: "Last Opened",
                sortable: true,
                render: (row) => date(row.lastOpenedAtUtcMilli, timeZone),
              },
            ]}
            showEmptyRow={!rows.length}
            emptyRowContent="No subscribers match your search."
            containerClassName="overflow-x-auto"
            tableSx={{
              minWidth: 960,
              "& th": {
                backgroundColor: "#FAFAFA",
                fontSize: 13,
                fontWeight: 700,
                padding: "13px 16px",
              },
              "& td": { padding: "14px 16px", fontSize: 14 },
            }}
          />
        </div>
      )}
      <ActionSuccessDialog
        message={exportSuccess}
        highlights={[campaignName]}
        onClose={() => setExportSuccess(null)}
      />
    </>
  );
};
